import { FC, ReactElement } from "react";
import { Card, Stack, Typography, TextField } from "@mui/material";
import { formatNumberWithSuffix } from "@/utils";

interface ISavingsCardProps {
  type: 'entryCost' | 'mpf' | 'duties';
  title: string;
  value: number;
  calculation?: string;
  goodsValue?: number;
  oldCostPerEntry?: number;
  newCostPerEntry?: number;
  onCostChange?: (type: 'old' | 'new', value: number) => void;
  oldEntrySummaries?: number;
  newEntrySummaries?: number;
}

const SavingsCard: FC<ISavingsCardProps> = ({ 
  type, 
  title, 
  value, 
  calculation, 
  goodsValue,
  oldCostPerEntry,
  newCostPerEntry,
  onCostChange,
  oldEntrySummaries,
  newEntrySummaries
}): ReactElement => {
  return (
    <Card sx={{ 
      p: 2, 
      height: "100%", 
      width: "100%",
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Stack spacing={2} height="100%">
        <Typography
          variant="body1"
          fontFamily="Manrope"
          fontWeight={800}
          color="black"
          fontSize="clamp(14px, 1vw, 16px)"
          noWrap
        >
          {title}
        </Typography>
        
        <Stack 
          spacing={0.5} 
          alignItems="center" 
          justifyContent="center"
          flex={1}
        >
          <Typography
            variant="h3"
            fontSize="clamp(32px, 2.5vw, 44px)"
            fontWeight={700}
            color="#0B494B"
            fontFamily="Plus Jakarta Sans"
            textAlign="center"
            sx={{ 
              wordBreak: "break-word",
              lineHeight: 1.2
            }}
          >
            ${formatNumberWithSuffix(value)}
          </Typography>
          
          {type === 'entryCost' && oldEntrySummaries && newEntrySummaries && (
            <Typography
              variant="caption"
              color="grey.600"
              sx={{ 
                fontSize: 'clamp(11px, 0.8vw, 13px)',
                whiteSpace: 'nowrap'
              }}
              fontWeight={600}
            >
              ${formatNumberWithSuffix(oldCostPerEntry! * oldEntrySummaries)} → ${formatNumberWithSuffix(newCostPerEntry! * newEntrySummaries)}
            </Typography>
          )}
        </Stack>

        {type === 'entryCost' && onCostChange && (
          <Stack spacing={1} sx={{ mt: 'auto' }}>
            <TextField
              size="small"
              label="Old Cost per Entry"
              type="number"
              value={oldCostPerEntry}
              onChange={(e) => onCostChange('old', Number(e.target.value))}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
            <TextField
              size="small"
              label="New Cost per Entry"
              type="number"
              value={newCostPerEntry}
              onChange={(e) => onCostChange('new', Number(e.target.value))}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Stack>
        )}
        
        {calculation && type !== 'entryCost' && (
          <Typography
            variant="body1"
            color="grey.600"
            sx={{ 
              fontSize: 'clamp(14px, 0.9vw, 16px)',
              whiteSpace: 'nowrap'
            }}
            textAlign="center"
            fontWeight={600}
          >
            ${calculation}
          </Typography>
        )}
        {type === 'duties' && goodsValue && (
          <Typography
            variant="body1"
            color="grey.600"
            sx={{ 
              fontSize: 'clamp(14px, 0.9vw, 16px)',
              whiteSpace: 'nowrap'
            }}
            textAlign="center"
            fontWeight={600}
          >
            Goods Value: ${formatNumberWithSuffix(goodsValue)}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

export default SavingsCard; 